import * as React from "react";
import {Control, Field, Icon, Input, Radio, TextArea} from "bloomer";
import Card from "../../../elements/Card";
import {getArticleKeywordsByText, getArticleKeywordsByURL} from "../../../api/models/ArticleKeywords";
import Button from "../../../elements/Button";
import getWordsCount from "../../../utils/WordsCount";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import WordsLabel from "../../../elements/WordsLabel";
import SectionName from "../../../elements/SectionName";
import Spacer from "../../../elements/Spacer";
import FieldDescription from "../../../elements/FieldDescription";
import InputWarning from "../../../elements/InputWarning";
import InformationCard from "../../../elements/InformationCard";
import KeywordsHeader from "../../../components/model/ArticleKeywords/KeywordsHeader";
import KeywordsList from "../../../components/model/ArticleKeywords/KeywordsList";
import KeywordOccurrence from "../../../components/model/ArticleKeywords/KeywordOccurrence";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {INVALID_FIELD_STYLE, URL_REGEX} from "../../../utils/Constants";

interface IArticleKeywordsState {
    loading: boolean;
    inputType: string;
    textInput: string;
    textInputEmpty: boolean;
    urlInput: string
    urlInputEmpty: boolean;
    urlInputInvalid: boolean;
    result: any;
    wordsCount: number;
    showLimitWarning: boolean;
    errorMessage: string;
}

class ArticleKeywords extends React.Component<{}, IArticleKeywordsState> {
    public WORDS_LIMIT = 2000;

    constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            inputType: "url",
            textInput: "",
            textInputEmpty: false,
            urlInput: "",
            urlInputEmpty: false,
            urlInputInvalid: false,
            result: "",
            wordsCount: 0,
            showLimitWarning: false,
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.ARTICLE_KEYWORDS);
    }

    public handleSelectInputTypeURL = () => {
        this.setState({
            inputType: "url",
            textInput: "",
            textInputEmpty: false,
            urlInput: "",
            urlInputInvalid: false,
            urlInputEmpty: false,
            result: "",
            errorMessage: ""
        });
    };

    public handleSelectInputTypeText = () => {
        this.setState({
            inputType: "text",
            textInput: "",
            textInputEmpty: false,
            urlInput: "",
            urlInputInvalid: false,
            urlInputEmpty: false,
            result: "",
            errorMessage: ""
        });
    };

    public updateWordsCount = async () => {
        const wordsCount = getWordsCount(this.state.textInput.trim());
        this.setState({
            wordsCount,
            showLimitWarning: wordsCount > this.WORDS_LIMIT
        });
    };

    public handleURLInputChange = (e: any) => {
        const value = e.target.value;

        let urlInputEmpty = false;
        let valid = true;
        if (value.trim()) {
            valid = URL_REGEX.test(value.trim());
        } else {
            urlInputEmpty = true;
        }

        this.setState({
            urlInput: value,
            urlInputInvalid: !valid,
            urlInputEmpty,
            errorMessage: ""
        });
    };

    public handleTextInputChange = (e: any) => {
        this.setState({
            textInput: e.target.value,
            textInputEmpty: false,
            errorMessage: ""
        }, () => {
            this.updateWordsCount();
        });
    };

    public processResponse = (response: any) => {

        let result: any = null;
        if (response && response.data && response.data.keywords) {
            const data = response.data.keywords;
            const resultArr = Object.keys(data).map((keyword) => {
                return {
                    keyword,
                    data: data[keyword]
                };
            });
            if (resultArr.length) {
                result = resultArr;
            }
        } else {
            this.setState({
                loading: false,
                errorMessage: "Unable to get keywords."
            });
        }

        if (result) {
            this.setState({
                loading: false,
                result
            });
        }
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (this.state.inputType === "url") {
            await this.onSubmitURL();
        } else {
            await this.onSubmitText();
        }
        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.ARTICLE_KEYWORDS);
    }

    public onSubmitText = async () => {
        if (!this.state.textInput.trim() || this.state.loading || this.state.showLimitWarning) {
            this.setState({result: ""});
            if (!this.state.textInput.trim()) {
                this.setState({
                    textInputEmpty: true
                });
            }
            return;
        }

        this.setState({
            loading: true,
            result: "",
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getArticleKeywordsByText(this.state.textInput.trim()).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to get keywords."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        this.processResponse(response);
    };

    public onSubmitURL = async () => {
        if (!this.state.urlInput.trim() || this.state.urlInputInvalid || this.state.loading) {
            this.setState({result: ""});
            if (!this.state.urlInput.trim()) {
                this.setState({
                    urlInputEmpty: true,
                    urlInputInvalid: false
                });
            }
            return;
        }

        this.setState({
            loading: true,
            result: "",
            errorMessage: ""
        });

        let errorOccurred = false;

        const response = await getArticleKeywordsByURL(this.state.urlInput.trim()).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to get keywords."
                });
            }

            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        this.processResponse(response);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            textInput: "",
            textInputEmpty: false,
            urlInput: "",
            urlInputInvalid: false,
            urlInputEmpty: false,
            result: "",
            wordsCount: 0,
            showLimitWarning: false,
            errorMessage: ""
        });
    };

    public render() {
        return (
            <ModelContentContainer>
                <Card>
                    <SectionName>Input Type</SectionName>
                    <Field>
                        <Control>
                            <Radio name="url" checked={this.state.inputType === "url"}
                                   onChange={this.handleSelectInputTypeURL}> URL </Radio>
                            <Radio name="text" checked={this.state.inputType === "text"}
                                   onChange={this.handleSelectInputTypeText}> Text </Radio>
                        </Control>
                    </Field>
                    <Spacer/>
                    {
                        this.state.inputType === "text" ? <div>
                            <SectionName>Text</SectionName>
                            <FieldDescription>Maximum size of input is 2000 words.</FieldDescription>
                            <Field>
                                <Control>
                                    <TextArea
                                        style={(this.state.textInputEmpty || this.state.showLimitWarning) ? INVALID_FIELD_STYLE : undefined}
                                        onChange={this.handleTextInputChange}
                                        onPaste={this.handleTextInputChange}
                                        value={this.state.textInput}
                                        placeholder="Enter article text here"
                                    />
                                    <div>
                                        {
                                            this.state.textInputEmpty &&
                                            <InputWarning>Please input a text.</InputWarning>
                                        }
                                        {
                                            this.state.showLimitWarning &&
                                            <InputWarning>Input size limit has been exceeded. Please decrease the
                                                size.</InputWarning>
                                        }
                                        <WordsLabel>{this.state.wordsCount}&nbsp;{this.state.wordsCount > 1 ? 'words' : 'word'}</WordsLabel>
                                    </div>
                                </Control>
                            </Field>
                        </div> : <div>
                            <SectionName>URL</SectionName>
                            <Field>
                                <Control>
                                    <Input
                                        style={(this.state.urlInputEmpty || this.state.urlInputInvalid) ? INVALID_FIELD_STYLE : undefined}
                                        type="text"
                                        placeholder="Enter article url here"
                                        value={this.state.urlInput}
                                        onChange={this.handleURLInputChange}
                                    />
                                    <div>
                                        {
                                            this.state.urlInputEmpty && <InputWarning>Please input URL.</InputWarning>
                                        }
                                        {
                                            this.state.urlInputInvalid &&
                                            <InputWarning>Please input valid URL.</InputWarning>
                                        }
                                    </div>
                                </Control>
                            </Field>
                        </div>
                    }
                    <Spacer/>
                    <Spacer/>
                    <div>
                        <Button onClick={this.onSubmit}>Submit</Button>
                        <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                    </div>
                    {this.state.loading && (
                        <div>
                            <Spacer/>
                            <span>Loading...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div>
                    )}
                    {this.state.result && !this.state.loading && (
                        <div>
                            <Spacer/>
                            <SectionName>Keywords</SectionName>
                            <InformationCard>
                                <div>
                                    {
                                        this.state.result.map((item: any) => {
                                            return <div key={item.keyword}>
                                                <KeywordsHeader>{item.keyword}</KeywordsHeader>
                                                <KeywordsList>
                                                    {
                                                        item.data.map((k: any) => {
                                                            return <li key={k.keyword}>
                                                                <KeywordOccurrence>{k.keyword} - {k.occurence}</KeywordOccurrence>
                                                            </li>;
                                                        })
                                                    }
                                                </KeywordsList>
                                                <Spacer/>
                                            </div>
                                        })
                                    }
                                </div>
                            </InformationCard>
                        </div>
                    )}
                    {
                        this.state.errorMessage && !this.state.loading && (<div>
                            <Spacer/>
                            <InformationCard>{this.state.errorMessage}</InformationCard>
                        </div>)
                    }
                </Card>
            </ModelContentContainer>
        );
    }
}

export default ArticleKeywords;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/ArticleKeywords/ArticleKeywordsModel.tsx